/**
 * Unified Menu Component - Enhanced Navigation
 * 
 * Author: Iswe Solutions Team
 * Purpose: Modern, animated navigation with role-based access control
 * 
 * Features:
 * - Smooth animations and transitions
 * - Gradient accents and subtle shadows
 * - Icon badges for special sections
 * - Visual hierarchy with section cards
 */

import React, { useState, useEffect } from 'react';
import { useGetIdentity } from 'react-admin';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  Home as OverviewIcon,
  History as HistoryIcon,
  Speed as PipelineIcon,
  Memory as SystemHealthIcon,
  ViewInAr as ServicesIcon,
  Warning as ErrorsIcon,
  BarChart as DataQualityIcon,
  Insights as MonitoringIcon,
  WbSunny as WeatherIcon,
  Description as LogsIcon,
  Backup as BackupIcon,
  Schedule as ScheduleIcon,
  Pool as PoolIcon,
  Psychology as AIIcon,
  Storage as SourceIcon,
  LocationOn as LocationIcon,
  Tune as ConfigIcon,
  BugReport as AnomalyIcon,
  Timeline as TrendsIcon,
  MenuBook as DictionaryIcon,
  Assessment as ReportsIcon,
  Public as DashboardIcon,
  ExpandMore as ExpandIcon,
  Settings as SettingsIcon,
  AutoAwesome as SparkleIcon,
  Dns as InfrastructureIcon,
  Terminal as TerminalIcon,
  Folder as FolderIcon,
  NotificationsActive as EventsIcon,
  Layers as ImagesIcon,
  CloudUpload as UploadIcon,
  TableChart as TableChartIcon,
  ShowChart as VisualizerIcon,
  Code as CodeIcon,
  Biotech as DisaIcon,
  LibraryBooks as DocsIcon,
  Help as HelpIcon,
  Email as EmailIcon,
  Cached as CachedIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  QueryStats as QueryStatsIcon,
  SmartToy as SmartToyIcon,
  AdminPanelSettings as SecurityIcon,
  VpnKey as CredentialIcon,
  Api as ApiIcon,
  PersonAdd as OnboardIcon,
  DeveloperMode as DeveloperIcon,
  AutoGraph as DataStudioIcon,
  Coronavirus as AmrIcon,
  GridOn as AntibiogramIcon,
  Medication as AntibioticUseIcon,
  CrisisAlert as OutbreakRiskIcon,
  FactCheck as DataCheckIcon,
  Speed as ScorecardIcon,
  Healing as EmpiricIcon,
  NotificationImportant as AmrAlertIcon,
} from '@mui/icons-material';
import { APP_VERSION, BUILD_HASH, BUILD_DATE } from '../version';
import { hasPageAccess, MENU_SECTIONS, getDefaultPermissions } from '../config/menuConfig';
import { apiClient } from '../services/api';

// Doc tree node interface (matches /api/docs/tree response)
interface DocNode {
  name: string;
  path: string;
  type: 'file' | 'folder';
  children?: DocNode[];
}

// Brand colors
const COLORS = {
  primary: '#0d5c2e',
  primaryLight: '#148a44',
  accent: '#ef7d00',
  accentLight: '#ff9d3d',
  text: '#1e293b',
  textMuted: '#64748b',
  border: 'rgba(13, 92, 46, 0.12)',
};

// Menu section definitions
interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  badge?: string;
  adminOnly?: boolean;  // If true, only show to admin users
  permission?: string;  // Page-level permission (path used if not specified)
}

interface MenuSection {
  id: string;
  label: string;
  icon: React.ReactNode;
  requiredPermission: string;
  items: MenuItem[];
  gradient?: string;
  accentColor?: string;
  hidden?: boolean;
}

// Icon lookup maps — menuConfig.ts owns structure, this file owns visuals
const SECTION_ICONS: Record<string, React.ReactNode> = {
  overview: <OverviewIcon />,
  my_uploads: <UploadIcon />,
  data_management: <SourceIcon />,
  pipeline: <PipelineIcon />,
  infrastructure: <InfrastructureIcon />,
  monitoring: <SystemHealthIcon />,
  api_platform: <ApiIcon />,
  surveillance: <HealthAndSafetyIcon />,
  analysis: <QueryStatsIcon />,
  amr: <AmrIcon />,
  engine: <SmartToyIcon />,
  data_tools: <CodeIcon />,
  administration: <SettingsIcon />,
  documentation: <DocsIcon />,
};

const ITEM_ICONS: Record<string, React.ReactNode> = {
  // overview
  home: <DashboardIcon />,
  // data_management
  sources: <SourceIcon />,
  harmonization: <LocationIcon />,
  dictionary: <DictionaryIcon />,
  // my_uploads
  upload_portal: <UploadIcon />,
  // pipeline
  pipelines: <PipelineIcon />,
  extraction: <MonitoringIcon />,
  schedule: <ScheduleIcon />,
  quality: <DataQualityIcon />,
  // infrastructure
  services: <ServicesIcon />,
  terminal: <TerminalIcon />,
  files: <FolderIcon />,
  events: <EventsIcon />,
  images: <ImagesIcon />,
  // monitoring
  pools: <PoolIcon />,
  system: <SystemHealthIcon />,
  redis: <CachedIcon />,
  errors: <ErrorsIcon />,
  logs: <LogsIcon />,
  // api_platform
  gateway: <ApiIcon />,
  marketplace: <OnboardIcon />,
  developer: <DeveloperIcon />,
  'custom-endpoints': <ApiIcon />,
  'api-docs': <ConfigIcon />,
  // surveillance
  'intelligence-home': <AIIcon />,
  'alert-program': <EventsIcon />,
  'situation-reports': <HealthAndSafetyIcon />,
  'hotspot-map': <AnomalyIcon />,
  'outbreak-response': <TableChartIcon />,
  signals: <SparkleIcon />,
  'weekly-bulletin': <ReportsIcon />,
  // analysis
  'lab-analytics': <DisaIcon />,
  'weather-analytics': <WeatherIcon />,
  'climate-disease': <TrendsIcon />,
  'one-health': <HealthAndSafetyIcon />,
  'excess-mortality': <TrendsIcon />,
  // amr
  'amr-overview': <AmrIcon />,
  'amr-scorecard': <ScorecardIcon />,
  'amr-empirical-therapy': <EmpiricIcon />,
  'amr-alerts': <AmrAlertIcon />,
  'amr-drug-resistant-tb': <DisaIcon />,
  'amr-briefings': <ReportsIcon />,
  'amr-coverage': <LocationIcon />,
  'amr-climate': <WeatherIcon />,
  'amr-antibiogram': <AntibiogramIcon />,
  'amr-trends': <TrendsIcon />,
  'amr-outbreak-risk': <OutbreakRiskIcon />,
  'amr-one-health': <HealthAndSafetyIcon />,
  'amr-antibiotic-use': <AntibioticUseIcon />,
  'amr-data-quality': <DataCheckIcon />,
  'amr-literature': <ReportsIcon />,
  // engine
  'engine-overview': <SmartToyIcon />,
  'algorithm-catalog': <ConfigIcon />,
  'engine-monitoring': <QueryStatsIcon />,
  'threshold-config': <EventsIcon />,
  'analytics-explorer': <SparkleIcon />,
  'agent-monitor': <MonitoringIcon />,
  knowledge: <DictionaryIcon />,
  // data_tools
  'data-studio': <DataStudioIcon />,
  'sql-explorer': <CodeIcon />,
  'data-visualizer': <VisualizerIcon />,
  'event-visualizer': <EventsIcon />,
  'outlier-management': <AnomalyIcon />,
  // administration
  config: <ConfigIcon />,
  'system-config': <SettingsIcon />,
  alerts: <EventsIcon />,
  email: <EmailIcon />,
  'disa-config': <DisaIcon />,
  security: <SecurityIcon />,
  credentials: <CredentialIcon />,
  audit: <HistoryIcon />,
  backup: <BackupIcon />,
  // documentation
  'technical-docs': <DocsIcon />,
  'api-reference': <ConfigIcon />,
  help: <HelpIcon />,
};

const DEFAULT_ICON = <DashboardIcon />;

// Derive menuSections from the single source of truth (menuConfig.ts)
// Adding only the visual icon layer here
const menuSections: MenuSection[] = MENU_SECTIONS.map(section => ({
  id: section.id,
  label: section.label,
  icon: SECTION_ICONS[section.id] || DEFAULT_ICON,
  requiredPermission: section.requiredPermission,
  gradient: section.gradient,
  accentColor: section.accentColor,
  hidden: section.hidden,
  items: section.items.map(item => ({
    id: item.id,
    label: item.label,
    icon: ITEM_ICONS[item.id] || DEFAULT_ICON,
    path: item.path,
    badge: item.badge,
    adminOnly: item.adminOnly,
    permission: item.permission,
  })),
}));

// Default permissions for fallback — derived from the single source of truth
const defaultPermissions = getDefaultPermissions('viewer');

// hasPageAccess is imported from config/menuConfig.ts

/**
 * Recursive doc tree menu item for the documentation section.
 * Renders folders as expandable sub-items and files as clickable links.
 */
const DocTreeItem: React.FC<{
  node: DocNode;
  level: number;
  accentColor: string;
  gradient: string;
  navigate: (to: string) => void;
  location: { pathname: string; search: string };
  expandedDocFolders: Set<string>;
  onToggleFolder: (path: string) => void;
  animIndex: number;
  isExpanded: boolean;
}> = ({ node, level, accentColor, gradient, navigate, location, expandedDocFolders, onToggleFolder, animIndex, isExpanded }) => {
  const isFolder = node.type === 'folder';
  const isFolderOpen = expandedDocFolders.has(node.path);
  const currentDocPath = new URLSearchParams(location.search).get('path') || '';
  const active = !isFolder && location.pathname === '/docs' && currentDocPath === node.path;
  // Clean display name: remove .md, replace dashes/underscores with spaces
  const displayName = node.name.replace(/\.md$/i, '').replace(/[-_]/g, ' ');

  return (
    <>
      <ListItem disablePadding sx={{
        animation: isExpanded ? 'slideIn 0.15s ease ' + (animIndex * 0.02) + 's both' : 'none',
      }}>
        <ListItemButton
          onClick={() => {
            if (isFolder) {
              onToggleFolder(node.path);
              // Navigate to folder's index.md if it exists
              const indexChild = node.children?.find(c => c.type === 'file' && c.path.toLowerCase().endsWith('/index.md'));
              if (indexChild) {
                navigate('/docs?path=' + encodeURIComponent(indexChild.path));
              }
            } else {
              navigate('/docs?path=' + encodeURIComponent(node.path));
            }
          }}
          sx={{
            borderRadius: '10px',
            py: 0.6,
            px: 1.5,
            pl: 1.5 + level * 1.5,
            my: 0.1,
            transition: 'all 0.2s ease',
            background: active ? alpha(accentColor, 0.1) : 'transparent',
            '&:hover': {
              background: alpha(accentColor, 0.05),
              transform: 'translateX(2px)',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 26, '& .MuiSvgIcon-root': { fontSize: 16, color: isFolder ? (isFolderOpen ? accentColor : COLORS.textMuted) : (active ? accentColor : COLORS.textMuted) } }}>
            {isFolder ? <FolderIcon /> : <DocsIcon />}
          </ListItemIcon>
          <ListItemText
            primary={displayName}
            primaryTypographyProps={{
              fontSize: 12.5,
              fontWeight: active ? 600 : isFolder ? 500 : 400,
              color: active ? accentColor : isFolder ? COLORS.text : COLORS.textMuted,
              noWrap: true,
            }}
          />
          {isFolder && (
            <ExpandIcon sx={{
              fontSize: 16,
              color: COLORS.textMuted,
              transition: 'transform 0.2s ease',
              transform: isFolderOpen ? 'rotate(180deg)' : 'rotate(0)',
            }} />
          )}
        </ListItemButton>
      </ListItem>
      {isFolder && node.children && (
        <Collapse in={isFolderOpen} timeout={200} unmountOnExit>
          {node.children.map((child, idx) => (
            <DocTreeItem
              key={child.path}
              node={child}
              level={level + 1}
              accentColor={accentColor}
              gradient={gradient}
              navigate={navigate}
              location={location}
              expandedDocFolders={expandedDocFolders}
              onToggleFolder={onToggleFolder}
              animIndex={idx}
              isExpanded={isFolderOpen}
            />
          ))}
        </Collapse>
      )}
    </>
  );
};

const UnifiedMenu: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: identity, isLoading } = useGetIdentity();
  
  const [expandedSections, setExpandedSections] = useState<string[]>(['overview']);
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);

  // Documentation tree state
  const [docTree, setDocTree] = useState<DocNode[]>([]);
  const [docTreeLoaded, setDocTreeLoaded] = useState(false);
  const [expandedDocFolders, setExpandedDocFolders] = useState<Set<string>>(new Set());

  // Get user from localStorage directly for reliable role check
  const getUserFromStorage = (): { role?: string; is_superuser?: boolean } | null => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) return JSON.parse(userStr);
    } catch (e) { /* ignore */ }
    return null;
  };
  
  const user = getUserFromStorage();
  const role = user?.role?.toLowerCase();
  const isAdmin = role === 'super_user' || role === 'admin' || role === 'superadmin' || user?.is_superuser === true;

  // Get user permissions from localStorage (most reliable, updated by /auth/me)
  const getUserPermissions = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const u = JSON.parse(userStr);
        if (u.permissions) return u.permissions;
      }
    } catch (e) { /* ignore */ }
    return null;
  };

  const permissions = getUserPermissions() || (identity as any)?.permissions || defaultPermissions;
  const allowedPages = isAdmin ? ['*'] : (permissions.pages || defaultPermissions.pages);

  // Section visibility is keyed on section.id, which is what RoleManagement
  // writes into menu_sections. It used to be compared against
  // requiredPermission, and those differ for six of the thirteen sections:
  // api_platform requires 'administration', documentation requires 'overview',
  // and surveillance / analysis / engine / data_tools all require 'analytics'.
  // Ticking one of those therefore granted its siblings, and unticking it
  // changed nothing.
  const explicitSections: string[] | undefined = permissions.menu_sections;
  const allowedSectionIds = isAdmin
    ? menuSections.map(s => s.id)
    : Array.isArray(explicitSections) && explicitSections.length > 0
      // An explicit list is the administrator's decision and wins outright.
      // Deriving sections from pages as well meant a single shared page put a
      // removed section back: a viewer holding /docs/api-reference, which is
      // filed under API Platform, was shown the whole API Platform header.
      ? explicitSections
      // Only a role with no list at all falls back to derivation, so roles
      // predating the section editor keep working.
      : menuSections
          .filter(section => section.items.some(
            item => item.path && hasPageAccess(item.path, allowedPages)
          ))
          .map(section => section.id);
  

  
  // Auto-expand section based on current path
  useEffect(() => {
    let activeSection = menuSections.find(section =>
      section.items.some(item => item.path && (
        location.pathname === item.path || 
        location.pathname.startsWith(item.path + '/')
      ))
    );
    // Also expand documentation section when on /docs
    if (!activeSection && location.pathname === '/docs') {
      activeSection = menuSections.find(s => s.id === 'documentation');
    }
    if (activeSection && !expandedSections.includes(activeSection.id)) {
      setExpandedSections([activeSection.id]);
    }
  }, [location.pathname]);

  // Load documentation tree when documentation section is expanded
  useEffect(() => {
    if (expandedSections.includes('documentation') && !docTreeLoaded) {
      apiClient.get<{ tree: DocNode[] }>('/docs/tree')
        .then(res => {
          setDocTree(res.data.tree || []);
          setDocTreeLoaded(true);
        })
        .catch(() => setDocTreeLoaded(true)); // fail silently
    }
  }, [expandedSections, docTreeLoaded]);

  // Toggle section
  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev: string[]) =>
      prev.includes(sectionId) ? [] : [sectionId]
    );
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    if (path === '/') return location.pathname === '/';
    
    // Handle hash-based routes (e.g., /docs#overview)
    if (path.includes('#')) {
      const [pathPart, hashPart] = path.split('#');
      const currentHash = location.hash.replace('#', '');
      return location.pathname === pathPart && currentHash === hashPart;
    }
    
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Filter sections based on menu-level permissions
  // Then filter items within each section based on page-level permissions
  const visibleSections = menuSections
    .filter(section => !section.hidden)
    .filter(section => isAdmin || allowedSectionIds.includes(section.id))
    .map(section => ({
      ...section,
      items: section.items.filter(item => {
        // Admin sees everything
        if (isAdmin) return true;
        // Admin-only items are dropped here rather than only at render time.
        // Filtering them later left sections whose every remaining item was
        // admin-only counting as non-empty, so the header survived with
        // nothing under it.
        if (item.adminOnly) return false;
        // An item naming no page is not implicitly public.
        const pagePath = item.permission || item.path;
        if (!pagePath) return false;
        return hasPageAccess(pagePath, allowedPages);
      })
    }))
    .filter(section => section.items.length > 0);  // Remove empty sections

  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        py: 6,
        flexDirection: 'column',
        gap: 2,
      }}>
        <CircularProgress size={28} sx={{ color: COLORS.primary }} />
        <Typography variant="caption" color="text.secondary">Loading menu...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '100%',
      // height, not minHeight. minHeight lets this grow past the sidebar, so
      // the section list below never becomes scrollable and the version badge
      // is pushed out of view instead of pinning to the bottom.
      height: '100%',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      background: '#ffffff',
    }}>
      {/* Menu Sections */}
      <Box sx={{
        flex: 1,
        // A flex child defaults to min-height:auto, which refuses to shrink
        // below its content. Without this the box simply grows to fit every
        // section and overflow:auto has nothing to scroll.
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        // Keep wheel/touch scrolling inside the menu rather than chaining to
        // the page once the list hits an end.
        overscrollBehavior: 'contain',
        py: 1,
        // 4px at 0.2 alpha was effectively invisible, so the list did not look
        // scrollable even when it was.
        '&::-webkit-scrollbar': { width: '8px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          background: alpha(COLORS.primary, 0.35),
          borderRadius: '4px',
          '&:hover': { background: alpha(COLORS.primary, 0.55) },
        },
        // Firefox
        scrollbarWidth: 'thin',
        scrollbarColor: alpha(COLORS.primary, 0.35) + ' transparent',
      }}>
        {visibleSections.map((section) => {
          const isExpanded = expandedSections.includes(section.id);
          const hasActiveItem = section.items.some(item => isActive(item.path))
            || (section.id === 'documentation' && location.pathname === '/docs');
          const isHovered = hoveredSection === section.id;
          const accentColor = section.accentColor || COLORS.primary;
          
          return (
            <Box key={section.id} sx={{ mb: 0.5, mx: 1 }}>
              {/* Section Header Card */}
              <ListItem 
                disablePadding
                onMouseEnter={() => setHoveredSection(section.id)}
                onMouseLeave={() => setHoveredSection(null)}
              >
                <ListItemButton
                  onClick={() => {
                    if (section.items.length === 1 && section.items[0].path) {
                      navigate(section.items[0].path);
                    } else if (section.id === 'documentation') {
                      toggleSection(section.id);
                      navigate('/docs');
                    } else {
                      toggleSection(section.id);
                    }
                  }}
                  sx={{
                    borderRadius: '12px',
                    py: 1.25,
                    px: 1.5,
                    mb: 0.25,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    background: hasActiveItem 
                      ? alpha(accentColor, 0.08)
                      : isHovered 
                        ? alpha(accentColor, 0.04)
                        : 'transparent',
                    borderLeft: hasActiveItem ? '3px solid ' + accentColor : '3px solid transparent',
                    '&:hover': {
                      background: alpha(accentColor, 0.08),
                      transform: 'translateX(2px)',
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: '3px',
                      background: section.gradient,
                      opacity: isHovered && !hasActiveItem ? 0.6 : 0,
                      transition: 'opacity 0.25s ease',
                    },
                  }}
                >
                  {/* Icon with gradient background */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      mr: 1.5,
                      background: hasActiveItem || isHovered
                        ? section.gradient
                        : alpha(accentColor, 0.1),
                      transition: 'all 0.25s ease',
                      boxShadow: hasActiveItem 
                        ? '0 4px 12px ' + alpha(accentColor, 0.3)
                        : 'none',
                      '& .MuiSvgIcon-root': {
                        fontSize: 20,
                        color: hasActiveItem || isHovered ? '#fff' : accentColor,
                        transition: 'color 0.25s ease',
                      },
                    }}
                  >
                    {section.icon}
                  </Box>
                  
                  <ListItemText 
                    primary={section.label}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: hasActiveItem ? 700 : 600,
                      color: hasActiveItem ? accentColor : COLORS.text,
                      letterSpacing: '-0.01em',
                    }}
                  />
                  
                  {section.items.length > 1 && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 24,
                        height: 24,
                        borderRadius: '6px',
                        transition: 'all 0.25s ease',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        background: isExpanded ? alpha(accentColor, 0.1) : 'transparent',
                      }}
                    >
                      <ExpandIcon 
                        sx={{ 
                          fontSize: 18, 
                          color: isExpanded ? accentColor : COLORS.textMuted,
                        }} 
                      />
                    </Box>
                  )}
                </ListItemButton>
              </ListItem>

              {/* Section Items */}
              {section.items.length > 1 && (
                <Collapse in={isExpanded} timeout={300} unmountOnExit>
                  <List 
                    component="div" 
                    disablePadding 
                    sx={{ 
                      py: 0.5,
                      pl: 1,
                      ml: 2.5,
                      borderLeft: '2px solid ' + alpha(accentColor, 0.15),
                    }}
                  >
                    {/* Documentation section: render doc tree dynamically */}
                    {section.id === 'documentation' && docTreeLoaded && (
                      <>
                        {docTree.map((node, idx) => (
                          <DocTreeItem
                            key={node.path}
                            node={node}
                            level={0}
                            accentColor={accentColor}
                            gradient={section.gradient || ''}
                            navigate={navigate}
                            location={location}
                            expandedDocFolders={expandedDocFolders}
                            onToggleFolder={(path) => setExpandedDocFolders(prev => {
                              const next = new Set(prev);
                              next.has(path) ? next.delete(path) : next.add(path);
                              return next;
                            })}
                            animIndex={idx}
                            isExpanded={isExpanded}
                          />
                        ))}
                        {/* Static items: API Reference, Help */}
                        {section.items
                          .filter(item => !item.adminOnly || isAdmin)
                          .map((item, itemIndex) => {
                          const active = isActive(item.path);
                          return (
                            <ListItem 
                              key={item.id} 
                              disablePadding
                              sx={{
                                animation: isExpanded ? 'slideIn 0.2s ease ' + ((docTree.length + itemIndex) * 0.03) + 's both' : 'none',
                                '@keyframes slideIn': {
                                  from: { opacity: 0, transform: 'translateX(-8px)' },
                                  to: { opacity: 1, transform: 'translateX(0)' },
                                },
                              }}
                            >
                              <ListItemButton
                                onClick={() => item.path && navigate(item.path)}
                                sx={{
                                  borderRadius: '10px',
                                  py: 0.875,
                                  px: 1.5,
                                  my: 0.25,
                                  position: 'relative',
                                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                  background: active ? alpha(accentColor, 0.1) : 'transparent',
                                  '&:hover': {
                                    background: active 
                                      ? alpha(accentColor, 0.15)
                                      : alpha(accentColor, 0.05),
                                    transform: 'translateX(4px)',
                                  },
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 32, '& .MuiSvgIcon-root': { fontSize: 18, color: active ? accentColor : COLORS.textMuted } }}>
                                  {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                  primary={item.label}
                                  primaryTypographyProps={{
                                    fontSize: 13,
                                    fontWeight: active ? 600 : 450,
                                    color: active ? accentColor : COLORS.textMuted,
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </>
                    )}
                    {section.id === 'documentation' && !docTreeLoaded && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={20} sx={{ color: accentColor }} />
                      </Box>
                    )}

                    {/* Other sections: render static items as before */}
                    {section.id !== 'documentation' && section.items
                      .filter(item => !item.adminOnly || isAdmin)
                      .map((item, itemIndex) => {
                      const active = isActive(item.path);
                      return (
                        <ListItem 
                          key={item.id} 
                          disablePadding
                          sx={{
                            animation: isExpanded ? 'slideIn 0.2s ease ' + (itemIndex * 0.03) + 's both' : 'none',
                            '@keyframes slideIn': {
                              from: { opacity: 0, transform: 'translateX(-8px)' },
                              to: { opacity: 1, transform: 'translateX(0)' },
                            },
                          }}
                        >
                          <ListItemButton
                            onClick={() => item.path && navigate(item.path)}
                            sx={{
                              borderRadius: '10px',
                              py: 0.875,
                              px: 1.5,
                              my: 0.25,
                              position: 'relative',
                              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                              background: active ? alpha(accentColor, 0.1) : 'transparent',
                              '&:hover': {
                                background: active 
                                  ? alpha(accentColor, 0.15)
                                  : alpha(accentColor, 0.05),
                                transform: 'translateX(4px)',
                              },
                              '&::before': active ? {
                                content: '""',
                                position: 'absolute',
                                left: -9,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                background: accentColor,
                                boxShadow: '0 0 8px ' + alpha(accentColor, 0.5),
                              } : {},
                            }}
                          >
                            <ListItemIcon 
                              sx={{ 
                                minWidth: 32,
                                '& .MuiSvgIcon-root': {
                                  fontSize: 18,
                                  color: active ? accentColor : COLORS.textMuted,
                                  transition: 'color 0.2s ease',
                                },
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <span>{item.label}</span>
                                  {item.badge && (
                                    <Chip
                                      label={item.badge}
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        background: section.gradient,
                                        color: '#fff',
                                        '& .MuiChip-label': { px: 0.75 },
                                      }}
                                    />
                                  )}
                                </Box>
                              }
                              primaryTypographyProps={{
                                fontSize: 13,
                                fontWeight: active ? 600 : 450,
                                color: active ? accentColor : COLORS.textMuted,
                                letterSpacing: '-0.01em',
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Version Badge at Bottom - pinned, never scrolls with the sections */}
      <Box sx={{
        p: 1.5,
        borderTop: '1px solid ' + COLORS.border,
        // Without this the badge is treated as shrinkable and gets squeezed to
        // nothing once the section list is long.
        flexShrink: 0,
        background: '#ffffff',
      }}>
        <Tooltip 
          title={'Version ' + APP_VERSION + ' • Built ' + BUILD_DATE + (BUILD_HASH ? ' • ' + BUILD_HASH : '')}
          placement="right"
          arrow
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 1, 
            py: 0.75,
            px: 1.5,
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            background: alpha(COLORS.primary, 0.04),
            border: '1px solid ' + COLORS.border,
            '&:hover': {
              background: alpha(COLORS.primary, 0.08),
              borderColor: alpha(COLORS.primary, 0.2),
            },
          }}>
            <SparkleIcon sx={{ fontSize: 14, color: COLORS.accent }} />
            <Typography 
              variant="caption" 
              sx={{ 
                color: COLORS.primary,
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            >
              v{APP_VERSION}
            </Typography>
            {BUILD_HASH && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: COLORS.textMuted, 
                  fontSize: '0.6rem', 
                  fontFamily: 'monospace',
                }}
              >
                {BUILD_HASH.substring(0, 7)}
              </Typography>
            )}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default UnifiedMenu;
